import React, { useMemo, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { TrendingUp, ArrowUpRight, ArrowDownRight, ArrowRight, Star, Download, Trophy, Flame, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayAvatar } from '../../../mascot';
import { MasteryRing } from '../../shared/MasteryRing';
import { MODULES, getModuleIcon } from '../../../content';
import { getAllModuleTrends, getModuleTrendDelta } from '../../../utils/activityHelpers';
import { getMasteryLabel } from '../../../utils/formatters';
import { EmptyState } from '../../../components/shared/EmptyState';
import { AchievementsSection } from '../../../components/progress/AchievementsSection';
import { getProgressToNextLevel } from '../../../config/constants';
import { getLevelTitle } from '../../../utils/motivationalMessages';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { getBenchmarkContext } from '../shared/benchmarkUtils';
import { downloadCsv } from '../../../utils/csvExport';
interface VoortgangPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
}
type TabId = 'per-vak' | 'prestaties';
export function VoortgangPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner
}: VoortgangPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('per-vak');
  const groupNum = parseInt(selectedGroup) || 6;
  // Compute data for "Per vak" tab (from GrowthPage)
  const growthData = useMemo(() => {
    if (!selectedChild || !childData) return null;
    const trends = getAllModuleTrends(childData.familyMastery || {}, groupNum);
    const modules = MODULES.map(module => {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      const delta = getModuleTrendDelta(childData.familyMastery || {}, groupNum, module.id);
      return {
        module,
        mastery,
        trend: trends[module.id] || 'neutral',
        delta
      };
    }).filter(m => m.mastery > 0).sort((a, b) => b.mastery - a.mastery);
    const overallMastery = modules.length > 0 ? Math.round(modules.reduce((sum, m) => sum + m.mastery, 0) / modules.length) : 0;
    const strengths = modules.filter(m => m.mastery >= 60).slice(0, 3);
    const areasForGrowth = [...modules].reverse().filter(m => m.mastery < 50).slice(0, 3);
    return {
      modules,
      overallMastery,
      strengths,
      areasForGrowth
    };
  }, [selectedChild, childData, selectedGroup, getLearnerModuleMastery, groupNum]);
  // Compute data for "Prestaties" tab (from PrestatiePage)
  const prestatiesData = useMemo(() => {
    if (!selectedChild || !childData) {
      return {
        totalQuestionsAnswered: 0,
        masteredCount: 0,
        xp: 0,
        level: 1,
        progress: {
          current: 0,
          needed: 100,
          percent: 0
        },
        currentStreak: 0
      };
    }
    const totalQs = Object.keys(childData.questionStates || {}).length;
    let mastered = 0;
    MODULES.forEach(m => {
      const mastery = getLearnerModuleMastery(selectedChild.id, m.id);
      if (mastery >= 70) mastered++;
    });
    const xp = childData.stats.xp || 0;
    const level = childData.stats.level || 1;
    const progress = getProgressToNextLevel(xp);
    const currentStreak = childData.dailyStreak?.currentWeekDays || 0;
    return {
      totalQuestionsAnswered: totalQs,
      masteredCount: mastered,
      xp,
      level,
      progress,
      currentStreak
    };
  }, [selectedChild, childData, getLearnerModuleMastery]);
  const handleExportCsv = () => {
    if (!selectedChild || !growthData) return;
    const headers = ['Vak', 'Beheersing (%)', 'Niveau', 'Trend', 'Trend (%)'];
    const rows = growthData.modules.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.module.title}"`, m.mastery, `"${getMasteryLabel(m.mastery).label}"`, `"${trendText}"`, m.delta].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const safeName = selectedChild.displayName.replace(/[^a-zA-Z0-9]/g, '_');
    downloadCsv(csvContent, `voortgang_${safeName}`);
  };
  if (!selectedChild || !growthData) {
    return <PageLayout title="Voortgang" description="Bekijk de voortgang" data-id="element-4826">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-4827">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const overallBenchmark = getBenchmarkContext(growthData.overallMastery, groupNum);
  return <PageLayout title="Voortgang" description={`Bekijk de voortgang van ${selectedChild.displayName}`} data-id="element-4828">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4829">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-4830" />

        {/* Export CSV */}
        <div className="flex justify-end" data-id="element-4831">
          <button onClick={handleExportCsv} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-all border border-emerald-200 bg-white" title="Exporteer als CSV" data-id="element-4832">
            <Download className="w-4 h-4" data-id="element-4833" />
            <span className="hidden sm:inline" data-id="element-4834">Exporteer CSV</span>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 border-b border-slate-200" data-id="element-4835">
          <button onClick={() => setActiveTab('per-vak')} className={`px-4 py-3 text-sm font-bold transition-all relative ${activeTab === 'per-vak' ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4836">
            Per vak
            {activeTab === 'per-vak' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-4837" />}
          </button>
          <button onClick={() => setActiveTab('prestaties')} className={`px-4 py-3 text-sm font-bold transition-all relative ${activeTab === 'prestaties' ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4838">
            Prestaties
            {activeTab === 'prestaties' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-4839" />}
          </button>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-4840">
          {activeTab === 'per-vak' && <motion.div key="per-vak" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-8" data-id="element-4841">
              {/* Growth Story */}
              <div className="flex flex-col md:flex-row items-center gap-6 bg-emerald-50 p-6 rounded-3xl border border-emerald-100 shadow-sm" data-id="element-4842">
                <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center border border-emerald-200 shadow-sm flex-shrink-0" data-id="element-4843">
                  <DjayAvatar size="lg" emotion="proud" data-id="element-4844" />
                </div>
                <div className="flex-1 text-center md:text-left" data-id="element-4845">
                  <h2 className="text-2xl font-black text-emerald-900 mb-2" data-id="element-4846">
                    Groei-overzicht
                  </h2>
                  <p className="text-emerald-800 font-medium leading-relaxed" data-id="element-4847">
                    {selectedChild.displayName} heeft een gemiddelde beheersing
                    van{' '}
                    <span className="font-black text-emerald-600" data-id="element-4848">
                      {growthData.overallMastery}%
                    </span>{' '}
                    <span className={`font-bold ${getMasteryLabel(growthData.overallMastery).colorClass}`} data-id="element-4849">
                      ({getMasteryLabel(growthData.overallMastery).label})
                    </span>{' '}
                    over alle geoefende vakken.
                    {growthData.strengths.length > 0 && ` Vooral ${growthData.strengths[0].module.title.toLowerCase()} gaat erg goed!`}
                  </p>
                  <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg border text-sm font-bold ${overallBenchmark.bgClass} ${overallBenchmark.colorClass}`} data-id="element-4850">
                    <span data-id="element-4851">{overallBenchmark.emoji}</span>
                    <span data-id="element-4852">{overallBenchmark.description}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-4853">
                {/* Strengths */}
                <section data-id="element-4854">
                  <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-4855">
                    <Star className="w-5 h-5 text-amber-500" data-id="element-4856" />
                    Sterke punten
                  </h3>
                  <div className="space-y-3" data-id="element-4857">
                    {growthData.strengths.map((s, i) => {
                  const IconComp = getModuleIcon(s.module.icon);
                  return <motion.div key={s.module.id} initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1
                  }} data-id="element-4858">
                          <Card className="p-4 border-emerald-100 bg-white shadow-sm flex items-center gap-4" data-id="element-4859">
                            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-4860">
                              <IconComp className="w-6 h-6" data-id="element-4861" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-4862">
                              <h4 className="font-bold text-slate-900 truncate" data-id="element-4863">
                                {s.module.title}
                              </h4>
                              <div className="text-xs font-medium text-slate-500 truncate" data-id="element-4864">
                                {s.module.description}
                              </div>
                            </div>
                            <div className="flex-shrink-0" data-id="element-4865">
                              <MasteryRing value={s.mastery} size={48} color="emerald" data-id="element-4866" />
                            </div>
                          </Card>
                        </motion.div>;
                })}
                    {growthData.strengths.length === 0 && <EmptyState title="Nog geen sterke punten" message="Nog niet genoeg geoefend om sterke punten te bepalen." emotion="encouraging" colorScheme="emerald" data-id="element-4867" />}
                  </div>
                </section>

                {/* Areas for Growth */}
                <section data-id="element-4868">
                  <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-4869">
                    <TrendingUp className="w-5 h-5 text-indigo-500" data-id="element-4870" />
                    Hier kan {selectedChild.displayName} nog groeien
                  </h3>
                  <div className="space-y-3" data-id="element-4871">
                    {growthData.areasForGrowth.map((w, i) => {
                  const IconComp = getModuleIcon(w.module.icon);
                  return <motion.div key={w.module.id} initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1
                  }} data-id="element-4872">
                          <Card className="p-4 border-indigo-100 bg-white shadow-sm flex items-center gap-4" data-id="element-4873">
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-4874">
                              <IconComp className="w-6 h-6" data-id="element-4875" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-4876">
                              <h4 className="font-bold text-slate-900 truncate" data-id="element-4877">
                                {w.module.title}
                              </h4>
                              <div className="text-xs font-medium text-slate-500 truncate" data-id="element-4878">
                                Oefening baart kunst!
                              </div>
                            </div>
                            <div className="flex-shrink-0" data-id="element-4879">
                              <MasteryRing value={w.mastery} size={48} color="indigo" data-id="element-4880" />
                            </div>
                          </Card>
                        </motion.div>;
                })}
                    {growthData.areasForGrowth.length === 0 && <EmptyState title="Geen aandachtspunten" message="Geen specifieke aandachtspunten gevonden. Goed bezig!" emotion="celebrating" colorScheme="emerald" data-id="element-4881" />}
                  </div>
                </section>
              </div>

              {/* All Modules Detail */}
              <section className="pt-4" data-id="element-4882">
                <h3 className="text-lg font-black text-slate-900 mb-4" data-id="element-4883">
                  Alle geoefende vakken
                </h3>
                <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-4884">
                  <div className="divide-y divide-slate-100" data-id="element-4885">
                    {growthData.modules.map(m => {
                  const IconComp = getModuleIcon(m.module.icon);
                  const isGood = m.mastery >= 60;
                  const isWarning = m.mastery < 40;
                  const barColor = isGood ? 'bg-emerald-500' : isWarning ? 'bg-rose-500' : 'bg-amber-400';
                  const moduleBenchmark = getBenchmarkContext(m.mastery, groupNum);
                  return <div key={m.module.id} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-4886">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-4887">
                            <div className="flex items-center gap-4 sm:w-1/3" data-id="element-4888">
                              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0" data-id="element-4889">
                                <IconComp className="w-5 h-5" data-id="element-4890" />
                              </div>
                              <div data-id="element-4891">
                                <div className="font-bold text-slate-900" data-id="element-4892">
                                  {m.module.title}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-4893">
                                  <span className="text-xs font-medium text-slate-500" data-id="element-4894">
                                    {getMasteryLabel(m.mastery).label}
                                  </span>
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${moduleBenchmark.colorClass} ${moduleBenchmark.bgClass}`} data-id="element-4895">
                                    {moduleBenchmark.label}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex-1 flex items-center gap-4" data-id="element-4896">
                              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-4897">
                                <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${m.mastery}%`
                          }} transition={{
                            duration: 1,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${barColor}`} data-id="element-4898" />
                              </div>
                              <div className="w-12 text-right text-xl font-black text-slate-900" data-id="element-4899">
                                {m.mastery}%
                              </div>
                              <div className="w-24 flex justify-end" data-id="element-4900">
                                {m.trend === 'up' && <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4901">
                                    <ArrowUpRight className="w-3 h-3" data-id="element-4902" />{' '}
                                    {m.delta > 0 ? `+${m.delta}%` : 'Stijgt'}
                                  </span>}
                                {m.trend === 'down' && <span className="flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4903">
                                    <ArrowDownRight className="w-3 h-3" data-id="element-4904" />{' '}
                                    {m.delta < 0 ? `${m.delta}%` : 'Daalt'}
                                  </span>}
                                {m.trend === 'stable' && <span className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4905">
                                    <ArrowRight className="w-3 h-3" data-id="element-4906" /> Stabiel
                                  </span>}
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </Card>
              </section>
            </motion.div>}

          {activeTab === 'prestaties' && <motion.div key="prestaties" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-8" data-id="element-4907">
              {/* Level Progress Card */}
              <Card className="p-6 bg-white border-slate-200 shadow-sm overflow-hidden relative" data-id="element-4908">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none opacity-60" data-id="element-4909" />

                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-4910">
                  <div className="w-32 h-32 bg-emerald-50 rounded-3xl flex items-center justify-center border-2 border-emerald-100 shadow-inner flex-shrink-0 relative" data-id="element-4911">
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-amber-300 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-20" data-id="element-4912">
                      <span className="text-white font-black text-sm" data-id="element-4913">
                        {prestatiesData.level}
                      </span>
                    </div>
                    <DjayAvatar size="lg" emotion="proud" data-id="element-4914" />
                  </div>

                  <div className="flex-1 w-full space-y-4" data-id="element-4915">
                    <div data-id="element-4916">
                      <div className="flex items-center justify-between mb-1" data-id="element-4917">
                        <h3 className="text-xl font-black text-slate-800" data-id="element-4918">
                          {getLevelTitle(prestatiesData.level)}
                        </h3>
                        <span className="text-sm font-bold text-emerald-600" data-id="element-4919">
                          Level {prestatiesData.level}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 font-medium" data-id="element-4920">
                        Nog{' '}
                        {prestatiesData.progress.needed - prestatiesData.progress.current}{' '}
                        XP tot het volgende level
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-4921">
                      <div className="h-4 bg-slate-100 rounded-full overflow-hidden shadow-inner" data-id="element-4922">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-1000 ease-out relative" style={{
                      width: `${Math.max(5, prestatiesData.progress.percent)}%`
                    }} data-id="element-4923">
                          <div className="absolute inset-0 bg-white/20 w-full h-full" style={{
                        backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                        backgroundSize: '1rem 1rem'
                      }} data-id="element-4924" />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs font-bold text-slate-400" data-id="element-4925">
                        <span data-id="element-4926">{prestatiesData.xp} XP</span>
                        <span data-id="element-4927">
                          {prestatiesData.xp + (prestatiesData.progress.needed - prestatiesData.progress.current)}{' '}
                          XP
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4928">
                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4929">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0" data-id="element-4930">
                    <Flame className="w-5 h-5 text-orange-500" data-id="element-4931" />
                  </div>
                  <div data-id="element-4932">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4933">
                      Reeks
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-4934">
                      {prestatiesData.currentStreak} dagen
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4935">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-4936">
                    <Star className="w-5 h-5 text-blue-500" data-id="element-4937" />
                  </div>
                  <div data-id="element-4938">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4939">
                      Vragen
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-4940">
                      {prestatiesData.totalQuestionsAnswered}
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4941">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-4942">
                    <Trophy className="w-5 h-5 text-purple-500" data-id="element-4943" />
                  </div>
                  <div data-id="element-4944">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4945">
                      Meester
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-4946">
                      {prestatiesData.masteredCount} vakken
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4947">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-4948">
                    <Sparkles className="w-5 h-5 text-amber-500" data-id="element-4949" />
                  </div>
                  <div data-id="element-4950">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4951">
                      Totaal XP
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-4952">
                      {prestatiesData.xp}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Achievements Section */}
              <div className="pt-4" data-id="element-4953">
                <AchievementsSection state={childData!} totalQuestionsAnswered={prestatiesData.totalQuestionsAnswered} masteredCount={prestatiesData.masteredCount} currentStreak={prestatiesData.currentStreak} data-id="element-4954" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </PageLayout>;
}